/**
 * Event Recaps — list of event instances (R2-13): sortable by date / event /
 * Sales $, searchable by name. Pick two to compare (R2-07); editors can add
 * a new event. Flags recaps whose hours include names that match no LP
 * promoter, so Jarea can fix them in LeadPerfection.
 */
import { AlertCircle, AlertTriangle, ArrowDown, ArrowUp, GitCompare, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import { EventsNav } from "../../events/EventsNav";
import { fmtValue } from "../../events/recapFields";
import { fetchRecaps } from "../../lib/eventReporting";
import type { RecapListItem } from "../../lib/eventReporting";

type SortKey = "date" | "event" | "leads" | "sales" | "amount" | "com";
const COLS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: "event", label: "Event" },
  { key: "date", label: "Dates" },
  { key: "leads", label: "Leads", numeric: true },
  { key: "sales", label: "Sales #", numeric: true },
  { key: "amount", label: "Sales $", numeric: true },
  { key: "com", label: "Cost of Mktg", numeric: true },
];

function sortValue(r: RecapListItem, k: SortKey): string | number {
  switch (k) {
    case "date": return r.start_date;
    case "event": return r.display_name.toLowerCase();
    case "leads": return r.leads ?? -1;
    case "sales": return r.sales_count ?? -1;
    case "amount": return r.sales_amount ?? -1;
    case "com": return r.cost_of_marketing_pct ?? -1;
  }
}

const shortDate = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

export function EventRecapList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<RecapListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "date", dir: -1 });
  const [picked, setPicked] = useState<string[]>([]);

  useEffect(() => {
    fetchRecaps().then(setRows).catch((e: Error) => setError(e.message));
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows ?? [])
      .filter((r) => !needle || r.display_name.toLowerCase().includes(needle) || r.subsource.toLowerCase().includes(needle))
      .sort((a, b) => {
        const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
        return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
      });
  }, [rows, q, sort]);

  function toggle(id: string) {
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p.slice(-1), id]));
  }

  return (
    <div className="p-8 view-in overflow-y-auto h-full scroll-quiet">
      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-[28px] leading-tight" style={{ color: "var(--ink)" }}>Event Recaps</h1>
          <p className="text-[14.5px] font-serif italic mt-1" style={{ color: "var(--ink-3)" }}>
            Results, costs and assumptions for each event.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" disabled={picked.length !== 2}
            onClick={() => navigate(`/events/compare?${new URLSearchParams({ a: picked[0], b: picked[1] })}`)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border hairline font-serif text-[14px] t-base focus-ring disabled:opacity-40"
            style={{ color: "var(--ink)" }}
            title={picked.length === 2 ? "Compare the two selected events" : "Tick two events to compare them"}>
            <GitCompare size={14} /> Compare{picked.length ? ` (${picked.length}/2)` : ""}
          </button>
          {user?.has_event_editor && (
            <Link to="/events/new"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-serif text-[14px] t-base focus-ring"
              style={{ background: "var(--ink)", color: "var(--bg)" }}>
              <Plus size={14} strokeWidth={2} /> New event
            </Link>
          )}
        </div>
      </header>

      <EventsNav />

      {error && (
        <div className="rounded-md p-4 mb-4 font-mono text-[13px] flex items-start gap-2"
          style={{ background: "var(--oxblood-bg)", color: "var(--oxblood)", border: "1px solid var(--oxblood)" }}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>Failed to load events: {error}</span>
        </div>
      )}

      <label className="relative block max-w-sm mb-4">
        <span className="sr-only">Search events</span>
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ink-3)" }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events"
          className="w-full pl-8 pr-3 py-1.5 rounded-md border hairline text-[13.5px] focus-ring"
          style={{ background: "var(--bg-elev)", color: "var(--ink)" }} />
      </label>

      <section className="rounded-lg border hairline overflow-hidden" style={{ background: "var(--bg-elev)" }}>
        {!rows && !error ? (
          <div className="p-8 font-serif italic text-[14px]" style={{ color: "var(--ink-3)" }}>Loading events…</div>
        ) : shown.length === 0 ? (
          <div className="p-8 font-serif italic text-[14px]" style={{ color: "var(--ink-3)" }}>No events match.</div>
        ) : (
          <div className="overflow-x-auto scroll-quiet">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="text-[10.5px] font-mono border-b hairline" style={{ color: "var(--ink-3)" }}>
                  <th className="pl-5 pr-2 py-3 font-normal w-[1%]"><span className="sr-only">Select to compare</span></th>
                  {COLS.map((c) => {
                    const on = sort.key === c.key;
                    return (
                      <th key={c.key} className={`px-3 py-3 font-normal whitespace-nowrap ${c.numeric ? "text-right" : ""}`}
                        aria-sort={on ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
                        <button type="button" className="inline-flex items-center gap-1 uppercase tracking-[0.14em] focus-ring rounded-sm"
                          onClick={() => setSort({ key: c.key, dir: on ? (sort.dir === 1 ? -1 : 1) : c.key === "event" ? 1 : -1 })}>
                          {c.label}
                          {on && (sort.dir === 1 ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                        </button>
                      </th>
                    );
                  })}
                  <th className="px-5 py-3 font-normal uppercase tracking-[0.14em]">Recap</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.instance_id} className="border-b hairline last:border-0 t-base hover:bg-[var(--hover)]" style={{ color: "var(--ink)" }}>
                    <td className="pl-5 pr-2 py-2">
                      <input type="checkbox" checked={picked.includes(r.instance_id)} onChange={() => toggle(r.instance_id)}
                        aria-label={`Select ${r.display_name} to compare`} />
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/events/${r.instance_id}`} className="font-serif underline-offset-2 hover:underline focus-ring rounded-sm">
                        {r.display_name}
                      </Link>
                      {(r.unmatched_hours ?? 0) > 0 && (
                        <span title="Some hours are logged under a name that matches no LeadPerfection promoter"
                          className="ml-2 inline-flex align-middle" style={{ color: "var(--brass)" }}>
                          <AlertTriangle size={13} />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" style={{ color: "var(--ink-2)" }}>
                      {shortDate(r.start_date)}{r.end_date !== r.start_date ? ` – ${shortDate(r.end_date)}` : ""}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtValue(r.leads, "int")}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtValue(r.sales_count, "int")}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtValue(r.sales_amount, "money")}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtValue(r.cost_of_marketing_pct, "pct")}</td>
                    <td className="px-5 py-2 whitespace-nowrap text-[12px]">
                      {!r.computed_at ? (
                        <Pill tone="brass">Pending refresh</Pill>
                      ) : r.last_saved_at ? (
                        <Pill tone="forest">Saved {new Date(r.last_saved_at).toLocaleDateString("en-US")}</Pill>
                      ) : (
                        <Pill tone="slate">Assumptions not entered</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Pill({ tone, children }: { tone: "forest" | "brass" | "slate"; children: ReactNode }) {
  return (
    <span className="px-1.5 py-0.5 rounded-sm text-[10.5px] uppercase tracking-[0.08em]"
      style={{ background: `var(--${tone}-bg)`, color: `var(--${tone})` }}>
      {children}
    </span>
  );
}
